import { createSelector, createStructuredSelector } from 'reselect';
import { filter, flow, get, omit, property, some, sortBy, sumBy } from 'lodash';
import { DEFAULT_REPORT_YEAR, NDFL_FORM_ID } from 'modules/Taps/components/NDFL3/constants/forms';
import { routeMap } from 'router';
import { formValueSelector } from 'redux-form/immutable';

const selector = formValueSelector(NDFL_FORM_ID);
export const ndfl3PackageFormSelect = (state) => selector(state, `Ndfl3Package`);
const tapsSelect = (state) => state.taps;
const filterZeroSection = (sectionOne) =>
  filter(sectionOne, (section) => get(section, 'podlUpl') || get(section, 'podlVozv'));
const hrefPaymentCreator = (cardId) => {
  return {
    pathname: routeMap.taxesPayment,
    query: {
      taxType: 'NDFL3',
      ndfl3Id: cardId,
    },
  };
};

const hrefRefundCreator = (cardId) => {
  return {
    pathname: routeMap.disposeOfOverpayment,
    query: {
      declarationId: cardId,
    },
  };
};

export const hrefResultSelector = createSelector(property('cardId'), (cardId) => {
  return {
    hrefRefund: hrefRefundCreator(cardId),
    hrefPayment: hrefPaymentCreator(cardId),
  };
});

export const showLinksSelector = createStructuredSelector({
  linkRefund: flow(
    property('payload'),
    (payload) =>
      get(payload, 'initialData.reportYear') < 2020 &&
      some(filterZeroSection(get(payload, 'sectionOne')), { prUplVozv: '2' }),
  ),
  linkPayment: flow(property('payload.sectionOne'), (sectionOne) =>
    some(filterZeroSection(sectionOne), { prUplVozv: '1' }),
  ),
});

export const selectNDFL3 = createSelector(tapsSelect, ({ ndfl3 }) => ndfl3);

export const selectXMLDraft = createSelector(tapsSelect, ({ ndfl3: { xmlDraft } }) => xmlDraft);

export const selectNDFL2 = createSelector(selectNDFL3, ({ ndfl2 }) => ndfl2);

export const selectNDFL3Package = createSelector(selectNDFL3, ({ ndfl3Package }) => ndfl3Package);
export const selectNDFL3SectionOne = createSelector(
  selectNDFL3Package,
  property('payload.sectionOne'),
);

export const selectSubmitFailedNDFL3 = createSelector(
  selectNDFL3,
  ({ submitFailedNDFL3 }) => submitFailedNDFL3,
);

export const selectNdfl3Attachments = createSelector(ndfl3PackageFormSelect, (form) =>
  form.getIn(['payload', 'attaches']),
);

export const selectNdfl3TaxInfo = createSelector(ndfl3PackageFormSelect, (form) =>
  form.get('taxInspectionCode'),
);

export const selectNdfl3nomKorr = createSelector(ndfl3PackageFormSelect, (form) =>
  form.getIn(['payload', 'initialData', 'nomKorr']),
);

export const selectAllowedFileCategories = createSelector(selectNDFL3Package, ({ payload }) =>
  get(payload, 'allowedFileCategories', []),
);
export const selectFileCategories = createSelector(
  selectNDFL3,
  selectAllowedFileCategories,
  ({ dictionaries: { fileCategories: { data = [] } = {} } }, allowedFileCategories) =>
    sortBy(
      data.filter(({ docType }) => allowedFileCategories.includes(docType)),
      ['sortOrder', 'docType', 'documentDescription'],
    ),
);
export const reportYear = createSelector(selectNDFL3Package, ({ payload }) => {
  return get(payload, 'initialData.reportYear', DEFAULT_REPORT_YEAR);
});

export const selectNDFL3ReturnSum = createSelector(selectNDFL3Package, ({ payload }) =>
  sumBy(get(payload, 'sectionOne', []), 'podlVozv'),
);

export const selectDictionaries = createSelector(selectNDFL3, ({ dictionaries }) => dictionaries);

export const selectTaxDeductionOwnerCodes = createSelector(
  selectDictionaries,
  ({ taxDeductionOwnerCodes: { data = [] } } = {}) => data,
);

export const selectStdDeductionsBy2NDFL = createSelector(
  selectNDFL3,
  ({ stdDeductionsBy2NDFL }) => stdDeductionsBy2NDFL,
);

export const selectMonthIncomes = createSelector(
  selectNDFL3,
  ({ monthIncomes = {} }) => monthIncomes,
);

export const selectMonthIncomesError = createSelector(selectMonthIncomes, ({ error }) => error);

export const selectDeductionsSelector = createSelector(selectNDFL3, (ndfl3) =>
  omit(get(ndfl3, 'ndfl3Package.payload.deductionSelector'), 'formId'),
);

export const selectNdfl3AttachesSubmitting = createSelector(
  selectNDFL3,
  ({ updating3NDFLDraftAttaches: { loading } }) => loading,
);

export const selectSaveDraftError = createSelector(selectNDFL3, ({ error }) => error);

export const selectDetailsReturnFunds = createSelector(
  tapsSelect,
  ({ detailsReturnFunds }) => detailsReturnFunds,
);

export const selectSignatureType = createSelector(tapsSelect, ({ certificateInfo }) =>
  get(certificateInfo.get('data'), 'signatureType'),
);
